import { useRef } from 'react';

export type VectorClock = [number, number];

/**
 * Per-component vector-clock helper for value-bearing components.
 *
 * The clock is `[server_count, client_count]`:
 *   - `server_count` advances on the server when an agent calls
 *     `var.set(...)` server-side; the server is authoritative for that
 *     count.
 *   - `client_count` advances on the *client* on every local edit; the
 *     client is authoritative for that count.
 *
 * Every outgoing `ComponentMsg` from the server carries the server's
 * view of `client_count`. The client gates incoming `value` updates on
 * that matching its local count — otherwise the server's snapshot was
 * computed before our latest edit reached it, and applying it would
 * clobber pending work. On a successful accept we copy the *whole*
 * tuple from the server, so subsequent outgoing events report the
 * latest server count too.
 *
 * Usage in a component:
 *
 *   const clock = useValueClock(__clock);
 *   useEffect(() => {
 *     if (!clock.isFresh()) return;
 *     clock.accept();
 *     setLocalValue(valueProp);
 *   }, [__clock, valueProp]);
 *
 *   // on user edit:
 *   sendEvent(__node_id, 'change', { kind: 'XChanged', value: v, _clock: clock.bump() });
 */
export function useValueClock(incoming: VectorClock | undefined) {
  const localRef = useRef<VectorClock>(incoming ?? [0, 0]);

  const isFresh = (): boolean =>
    !!incoming && incoming[1] === localRef.current[1];

  const accept = (): void => {
    if (incoming) localRef.current = [incoming[0], incoming[1]];
  };

  const bump = (): VectorClock => {
    localRef.current = [localRef.current[0], localRef.current[1] + 1];
    return localRef.current;
  };

  const current = (): VectorClock => localRef.current;

  return { isFresh, accept, bump, current };
}
